import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Sidebar, SidebarItem } from '@insights_module/components/Sidebar';
import { Spacer } from '@components/Spacer';
import { useShowDetailsContext } from '@insights_module/context/ShowDetailsContext';
import { GroupLinkedNamedEntity } from '@insights_module/types/graphql/Schema';
import { useShowDetailsTableData } from '@insights_module/hooks/useShowDetailsTableData';

const RowWrapper = tw.div`flex items-center p-[6px] text-[11px] font-medium h-[33px] gap-6`;
const OverviewOuterWrapper = tw.div`flex flex-col bg-transparent w-full h-full relative overflow-scroll`;
const NoResultsWrapper = tw.div`flex h-full bg-gray-2 p-3 text-[11px] rounded-lg text-text-and-icon`;
export const OpenCollapsedButton = tw.div`flex items-center justify-center bg-special rounded text-[11px] font-medium cursor-pointer px-2 py-[5px]`;

type EntityListHeader = {
  entityLabel: string;
  typeLabel: string;
  countLabel: string;
};

const EntityListHeader = ({ data }: { data: EntityListHeader }) => {
  return (
    <RowWrapper tw='text-text-and-icon-muted bg-gray-2 rounded'>
      <div tw='w-[60%] max-w-[140px]'>{data.entityLabel}</div>
      <div tw='w-[40%]'>{data.typeLabel}</div>
      <div tw='flex max-w-[40px] min-w-[40px] justify-end whitespace-nowrap'>{data.countLabel}</div>
    </RowWrapper>
  );
};

const EntityListItem = ({ entity }: { entity: GroupLinkedNamedEntity }) => {
  const { t } = useTranslation();
  const { selectedShowDetailsEntity, setSelectedShowDetailNamedEntity } = useShowDetailsContext();

  const handleSelectEntity = () => setSelectedShowDetailNamedEntity(entity);

  return (
    <RowWrapper>
      <button
        tw='underline inline w-full text-left whitespace-nowrap max-w-[140px] text-text-and-icon'
        css={[selectedShowDetailsEntity?.id === entity.id && tw`text-accent-1`]}
        title={entity.value}
        onClick={handleSelectEntity}>
        <div tw='truncate'>{entity.value}</div>
      </button>
      <div
        tw='w-[40%] text-text-and-icon-muted truncate'
        title={t(`insights.overview_page.trending_section.top_entities.types.${entity.type}`)}>
        {t(`insights.overview_page.trending_section.top_entities.types.${entity.type}`)}
      </div>
      <div tw='flex max-w-[40px] min-w-[40px] justify-end text-text-and-icon-muted'>
        {entity.audio_count}
      </div>
    </RowWrapper>
  );
};

const EntitiesList = ({ entities = [] }: { entities?: GroupLinkedNamedEntity[] | null }) => {
  const { t } = useTranslation();

  if (!entities || entities?.length === 0) {
    return <NoResultsWrapper>{t('insights.entities_overview.no_results')}</NoResultsWrapper>;
  }

  return (
    <>
      <EntityListHeader
        data={{
          entityLabel: t('insights.entities_overview.labels.entity'),
          typeLabel: t('insights.entities_overview.labels.type'),
          countLabel: t('insights.entities_overview.labels.num_of_mentions'),
        }}
      />
      {entities?.map((entity, index) => <EntityListItem key={index} entity={entity} />)}
    </>
  );
};

const EntitiesOverview = () => {
  const { t } = useTranslation();
  const { entities, isLoading } = useShowDetailsTableData();

  return (
    <SidebarItem
      isLoading={isLoading}
      header={<Sidebar.Title>{t('insights.entities_overview.title')}</Sidebar.Title>}>
      <OverviewOuterWrapper>
        <Spacer direction='vertical' size='sm' />
        <EntitiesList entities={entities} />
      </OverviewOuterWrapper>
    </SidebarItem>
  );
};

export { EntitiesOverview };
